import { useQuery } from "@tanstack/react-query";
import { llmService } from "../services/llmService";
import { useNewton } from "../store/NewtonContext";
import { getSessionIdFromName } from "../utils/getSessionIdFromName";

/**
 * Usage:
 * const { data: sessions, isFetching } = useLlmSessions({ userId: userData?.uid, llmEnv })
 */
export function useLlmSessions({ userId } = {}) {
  const { llmEnv } = useNewton();
  const { data, ...rest } = useQuery({
    enabled: !!userId && !!llmEnv,
    queryKey: ["getLlmSessions_key", userId, llmEnv],
    queryFn: async () => {
      const res = await llmService.getSessions({
        env: llmEnv,
        userId,
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch sessions: ${res.status}`);
      }

      const responseText = await res.text();
      if (!responseText) {
        return { sessions: [] };
      }

      // Parse the JSON string response as per API spec
      try {
        const parsedResponse = JSON.parse(responseText);
        const sessions = Array.isArray(parsedResponse.sessions)
          ? parsedResponse.sessions.map((session) => {
              const sessionId = getSessionIdFromName(session?.name);

              return {
                ...session,
                sessionId: sessionId,
              };
            })
          : [];

        return { ...parsedResponse, sessions };
      } catch (error) {
        throw new Error("Failed to parse sessions response");
      }
    },
    staleTime: 60 * 1000,
  });

  return { llmSessions: data, ...rest };
}
